import { z } from 'zod';

import {
  ProfileId,
  ShareLinkId,
  VaultId,
  LoginRevisionId,
  ShareSecretId,
  UUID,
} from 'backend-client-web';
import { TotpAlgorithm } from 'vault-crypto';

import { makeTypescriptEnumSchema } from '../../enumSchemas';
import { schemaISODateString } from '../../isISO8601';
import objectPassthrough from '../../objectPassthrough';
import { ISODateString } from '../../types';
import {
  isUnknownValuePlaceholder,
  makeUnknownValuePlaceholderSchema,
  WithUnknownValuePlaceholder,
} from '../../unknownValuePlaceholder';
import { ProtectedValue, Unprotect } from '../ProtectedValue';

import { CustomField, ProtectedCustomField } from './CustomField';
import { ElsterCertificate } from './ElsterCertificate';
import { ImportInfo } from './ImportInfo';
import { LoginAndroidApp } from './LoginAndroidApp';
import { LoginCreditCard } from './LoginCreditCard';
import { LoginSource, LoginUiType, schemaLoginSource, schemaLoginUiType } from './LoginEnums';
import { LoginScopeEntry, UnknownScopeEntry } from './LoginScopeEntry';
import { LoginWebsiteProtocol, schemaLoginWebsiteProtocol } from './LoginWebsiteProtocol';
import { Passkey } from './Passkey';

interface LoginWebsiteAttributesEntry {
  protocol?: WithUnknownValuePlaceholder<LoginWebsiteProtocol, undefined>;
}
const LoginWebsiteAttributesEntry = objectPassthrough({
  protocol: schemaLoginWebsiteProtocol.optional(),
});

interface LoginWebsiteAttributes {
  [normalizedWebsite: string]: LoginWebsiteAttributesEntry;
}

const LoginWebsiteAttributes = z.record(z.string(), LoginWebsiteAttributesEntry);

interface LoginSender {
  name: string;
  company?: string;
}

const LoginSender = objectPassthrough({
  name: z.string(),
  company: z.string().optional(),
});

interface ShareLink {
  id: ShareLinkId;
  createdAt: ISODateString;
  openedAt?: ISODateString;
  expiresAt?: ISODateString;
  secret: ProtectedValue;
}

const ShareLink = objectPassthrough({
  id: z.string(),
  createdAt: schemaISODateString,
  openedAt: schemaISODateString.optional(),
  expiresAt: schemaISODateString.optional(),
  secret: ProtectedValue,
});

interface ShareSecret {
  id: ShareSecretId;
  createdAt: ISODateString;
  // NOTE: The age recipient value is not confidential, but needs encryption for integrity.
  // Otherwise an attacker could sneak in a recipient value that the password is encrypted to.
  ageRecipient: ProtectedValue;
}

const ShareSecret = objectPassthrough({
  id: z.string(),
  createdAt: schemaISODateString,
  ageRecipient: ProtectedValue,
});

export const schemaTotpAlgorithm = makeTypescriptEnumSchema(TotpAlgorithm, TotpAlgorithm.unknown);

enum LeakedPasswordCheckSkippedReason {
  // Used as fallback value, to distinguish a fallback state from no reason being present.
  unknown = 'unknown',
  empty = 'empty',
  pin = 'pin',
}

interface LeakedPasswordCheckInfo {
  readonly leakCount: number;
  readonly checkedAt: ISODateString;
  readonly checkedPasswordContentId: UUID;
  readonly checkSkippedReason?: WithUnknownValuePlaceholder<LeakedPasswordCheckSkippedReason>;
  readonly leakedPasswordsChangeTime?: ISODateString;
}

const LeakedPasswordCheckInfo = objectPassthrough({
  leakCount: z.number(),
  checkedAt: schemaISODateString,
  checkedPasswordContentId: z.uuid(),
  checkSkippedReason: makeTypescriptEnumSchema(
    LeakedPasswordCheckSkippedReason,
    LeakedPasswordCheckSkippedReason.unknown,
  ).optional(),
  leakedPasswordsChangeTime: schemaISODateString.optional(),
});

enum PasswordPolicyCheckSkippedReason {
  // Used as fallback value, to distinguish a fallback state from no reason being present.
  unknown = 'unknown',
  empty = 'empty',
  pin = 'pin',
}

const schemaPasswordPolicyCheckSkippedReason = makeTypescriptEnumSchema(
  PasswordPolicyCheckSkippedReason,
  PasswordPolicyCheckSkippedReason.unknown,
);

interface PasswordPolicyCheckInfo {
  readonly status:
    | { type: 'checked'; result: boolean }
    | { type: 'skipped'; reason: WithUnknownValuePlaceholder<PasswordPolicyCheckSkippedReason> };
  readonly checkedAt: ISODateString;
  readonly checkedPasswordContentId: UUID;
  readonly passwordPolicyChangeTime: ISODateString;
}

const PasswordPolicyCheckInfo = objectPassthrough({
  status: z.discriminatedUnion('type', [
    objectPassthrough({
      type: z.literal('checked'),
      result: z.boolean(),
    }),
    objectPassthrough({
      type: z.literal('skipped'),
      reason: makeUnknownValuePlaceholderSchema(
        schemaPasswordPolicyCheckSkippedReason,
        PasswordPolicyCheckSkippedReason.unknown,
      ),
    }),
  ]),
  checkedAt: schemaISODateString,
  checkedPasswordContentId: z.uuid(),
  passwordPolicyChangeTime: schemaISODateString,
});

type PasswordOriginInfo =
  | {
      readonly type: 'userInput';
      readonly passwordContentId: UUID;
    }
  | {
      readonly type: 'generator';
      readonly passwordContentId: UUID;
    }
  | {
      readonly type: 'import';
      readonly passwordContentId: UUID;
    }
  | {
      readonly type: 'shareLink';
      readonly passwordContentId: UUID;
      // optional for forward compatibility, we might not reliably have an
      // email here available forever
      readonly senderEmail?: string;
    };

const PasswordOriginInfo = z.discriminatedUnion('type', [
  objectPassthrough({
    type: z.literal('userInput'),
    passwordContentId: z.uuid(),
  }),
  objectPassthrough({
    type: z.literal('generator'),
    passwordContentId: z.uuid(),
  }),
  objectPassthrough({
    type: z.literal('import'),
    passwordContentId: z.uuid(),
  }),
  objectPassthrough({
    type: z.literal('shareLink'),
    passwordContentId: z.uuid(),
    senderEmail: z.string().optional(),
  }),
]);

interface LoginWithoutHistory {
  readonly uiType: WithUnknownValuePlaceholder<LoginUiType>;
  /**
   * Named display name in UI, only stored as title in Login for backwards compatibility reasons.
   */
  readonly title: string;
  readonly displayHeadline?: string;
  readonly websites: string[];
  readonly websiteAttributes?: LoginWebsiteAttributes;
  readonly androidApps?: LoginAndroidApp[];
  readonly scope?: WithUnknownValuePlaceholder<LoginScopeEntry, UnknownScopeEntry>[];
  readonly username: string;
  readonly password: ProtectedValue;
  readonly note: string;
  readonly creationTime: ISODateString;
  // Set on login edit or restore, used for sorting
  readonly changeTime?: ISODateString;
  readonly editTime: ISODateString;
  readonly creationProfileId?: ProfileId;
  readonly editProfileId?: ProfileId;
  readonly creationSource?: WithUnknownValuePlaceholder<LoginSource, undefined>;
  readonly editSource?: WithUnknownValuePlaceholder<LoginSource, undefined>;
  readonly customFields: CustomField[];
  readonly totp?: ProtectedValue;
  readonly totpAlgorithm?: WithUnknownValuePlaceholder<TotpAlgorithm>;
  readonly totpDigits?: number;
  readonly totpPeriod?: number;
  readonly passkey?: Passkey;
  readonly tags?: string[];
  readonly creditCard?: LoginCreditCard;
  readonly elsterCertificate?: ElsterCertificate;
  readonly wifiSsid?: string;
  readonly sender?: LoginSender;
  readonly pendingInboxConfirmation?: boolean;
  readonly linkedViewSecrets?: boolean;
  readonly linkedFrom?: {
    // informational. this is the profile responsible for the creation of the link
    linkedByProfileId: ProfileId;
    // informational. if linking *from* a personal vault, this is the profile id that owns it
    originPersonalProfileId: ProfileId | null;

    // used for maintaining the link
    originVaultId: VaultId;
  };
  readonly linkedTo?: Array<{
    // informational. this is the profile responsible for the creation of the link
    linkedByProfileId: ProfileId;
    // informational. if linking *to* a personal vault, this is the profile id that owns it
    targetPersonalProfileId: ProfileId | null;

    // used for maintaining the link
    targetVaultId: VaultId;
    addTime: ISODateString;
    removeTime: ISODateString | null;
  }>;
  readonly shareLinks?: ShareLink[];
  readonly shareSecrets?: ShareSecret[];
  // For a short time logins had an `internal` field containing `history` and `shareLinks`. This field may be present in old logins for some users
  // readonly internal?:  LoginInternalInfo;
  readonly archiveInfo?: {
    archivedByProfileId?: ProfileId;
    archiveTime: ISODateString;
  };
  readonly leakedPasswordCheck?: LeakedPasswordCheckInfo;
  readonly passwordOrigin?: WithUnknownValuePlaceholder<PasswordOriginInfo, undefined>;
  readonly loginSummaryVersion?: number;
  readonly passwordPolicyCheck?: PasswordPolicyCheckInfo;
  readonly importInfo?: ImportInfo;

  // deprecated fields:
  // icon: string;
}

const LoginWithoutHistory = objectPassthrough({
  uiType: z.union([z.undefined().transform((_) => LoginUiType.login), schemaLoginUiType]),
  title: z.string(),
  displayHeadline: z.string().optional(),
  websites: z.array(z.string()).default([]),
  websiteAttributes: LoginWebsiteAttributes.optional(),
  androidApps: z.array(LoginAndroidApp).optional(),
  scope: z
    .array(makeUnknownValuePlaceholderSchema(LoginScopeEntry, { kind: 'unknown' as const }))
    .optional(),
  username: z.string(),
  password: ProtectedValue,
  note: z.string(),
  creationTime: schemaISODateString,
  changeTime: schemaISODateString.optional(),
  editTime: schemaISODateString,
  creationSource: schemaLoginSource.optional(),
  editSource: schemaLoginSource.optional(),
  creationProfileId: z.uuid().optional(),
  editProfileId: z.uuid().optional(),
  customFields: z.array(CustomField),
  totp: ProtectedValue.optional(),
  totpAlgorithm: schemaTotpAlgorithm.optional(),
  totpDigits: z.number().int().gte(1).optional(),
  totpPeriod: z.number().int().gte(1).optional(),
  passkey: Passkey.optional(),
  tags: z.array(z.string()).optional(),
  creditCard: LoginCreditCard.optional(),
  elsterCertificate: ElsterCertificate.optional(),
  wifiSsid: z.string().optional(),
  sender: LoginSender.optional(),
  pendingInboxConfirmation: z.boolean().optional(),
  linkedViewSecrets: z.boolean().optional(),
  linkedFrom: z
    .object({
      linkedByProfileId: z.uuid(),
      originPersonalProfileId: z.uuid().nullable(),
      originVaultId: z.uuid(),
    })
    .optional(),
  linkedTo: z
    .array(
      z.object({
        linkedByProfileId: z.uuid(),
        targetPersonalProfileId: z.uuid().nullable(),
        targetVaultId: z.uuid(),
        addTime: schemaISODateString,
        removeTime: schemaISODateString.nullable(),
      }),
    )
    .optional(),
  shareLinks: z.array(ShareLink).optional(),
  shareSecrets: z.array(ShareSecret).optional(),
  archiveInfo: z
    .object({
      archivedByProfileId: z.uuid().optional(),
      archiveTime: schemaISODateString,
    })
    .optional(),
  leakedPasswordCheck: LeakedPasswordCheckInfo.optional(),
  passwordOrigin: makeUnknownValuePlaceholderSchema(PasswordOriginInfo, undefined).optional(),
  loginSummaryVersion: z.number().int().optional(),
  passwordPolicyCheck: PasswordPolicyCheckInfo.optional(),
  importInfo: ImportInfo.optional(),

  // deprecated fields
  // icon: z.string().optional(),
}).overwrite((l) => {
  if ('internal' in l) {
    const { internal: _, ...rest } = l;
    return rest;
  }
  // For legacy standalone passkeys, move the passkey username to l.username to match the
  // new merged passkey format
  if (isUnknownValuePlaceholder(l.uiType) && l.uiType.unknownValueJson === '"passkey"') {
    return {
      ...l,
      uiType: LoginUiType.login,
      username: (l.username.trim() || l.passkey?.username.trim()) ?? '',
    };
  }
  return l;
});

type LoginRevision = LoginWithoutHistory & {
  readonly baseRevisionId?: LoginRevisionId;
};

const LoginRevision = LoginWithoutHistory.safeExtend({
  baseRevisionId: z.uuid().optional(),
});

interface LoginRevisionHistory {
  readonly currentRevisionId: LoginRevisionId;
  // Revisions are intentionally left unparsed (`unknown`). Recursively validating every revision is
  // the dominant cost of loading a vault, and revisions are only needed when the revision history
  // UI is opened. Call LoginRevisions.parse() to validate them before reading any revision fields.
  readonly revisions: {
    [id: string]: unknown;
  };
}

const LoginRevisionHistory = z.object({
  currentRevisionId: z.uuid(),
  revisions: z.record(
    z.string(),
    // Keep revision unknown, but still strip nested internal field.
    z.unknown().overwrite((rev) => {
      if (typeof rev !== 'object' || !rev || !('internal' in rev)) {
        return rev;
      }
      const { internal: _, ...rest } = rev;
      return rest;
    }),
  ),
});

// On-demand schema for the unparsed `revisions` map. Use LoginRevisions.parse() at
// the boundary where revision contents are actually read (e.g. the history UI).
const LoginRevisions = z.record(z.string(), LoginRevision);

type Login = LoginWithoutHistory & {
  readonly history?: LoginRevisionHistory;
};

const Login = LoginWithoutHistory.safeExtend({
  history: LoginRevisionHistory.optional(),
}).overwrite((l) => {
  if (l.totp) {
    return l;
  }

  const totpFieldIndex = l.customFields.findIndex(
    (cf) => cf.protected && cf.name.toLowerCase().includes('totp'),
  );

  if (totpFieldIndex === -1) {
    return l;
  }

  const totpField = l.customFields[totpFieldIndex] as ProtectedCustomField;
  const totp: ProtectedValue = totpField.value;

  return {
    ...l,
    customFields: l.customFields.toSpliced(totpFieldIndex, 1),
    totp,
  };
});

const LOGIN_WITHOUT_HISTORY_KEYS = [
  'uiType',
  'title',
  'displayHeadline',
  'websites',
  'websiteAttributes',
  'androidApps',
  'scope',
  'username',
  'password',
  'note',
  'creationTime',
  'changeTime',
  'editTime',
  'creationProfileId',
  'editProfileId',
  'creationSource',
  'editSource',
  'customFields',
  'totp',
  'totpAlgorithm',
  'totpDigits',
  'totpPeriod',
  'passkey',
  'tags',
  'creditCard',
  'elsterCertificate',
  'wifiSsid',
  'sender',
  'pendingInboxConfirmation',
  'linkedViewSecrets',
  'linkedFrom',
  'linkedTo',
  'shareLinks',
  'shareSecrets',
  'archiveInfo',
  'leakedPasswordCheck',
  'passwordOrigin',
  'loginSummaryVersion',
  'passwordPolicyCheck',
  'importInfo',
] as const;

const LOGIN_KEYS = [...LOGIN_WITHOUT_HISTORY_KEYS, 'history'] as const;

const LOGIN_REVISION_KEYS = [...LOGIN_WITHOUT_HISTORY_KEYS, 'baseRevisionId'] as const;

const emptyUnprotectedLogin: Unprotect<Login> = {
  uiType: LoginUiType.login,
  creationTime: '',
  editTime: '',
  title: '',
  username: '',
  password: '',
  note: '',
  customFields: [],
  websites: [],
  totp: '',
};

export {
  Login,
  LoginWebsiteProtocol,
  LoginWebsiteAttributesEntry,
  LoginWebsiteAttributes,
  ShareLink,
  ShareSecret,
  LoginRevision,
  LoginWithoutHistory,
  LOGIN_KEYS,
  LOGIN_WITHOUT_HISTORY_KEYS,
  LOGIN_REVISION_KEYS,
  LoginRevisionHistory,
  LoginRevisions,
  emptyUnprotectedLogin,
  LeakedPasswordCheckInfo,
  LeakedPasswordCheckSkippedReason,
  PasswordOriginInfo,
  PasswordPolicyCheckInfo,
  PasswordPolicyCheckSkippedReason,
};
